import React from "react";
import { isCarrierAccessEnabled } from "@shared/config/featureFlags";
import {
  backhaulSummary,
  type BackhaulGroup,
  type CarrierAccess,
} from "kontrolEdge/helpers/carrierAccess";
import { DetailLink } from "../Shared/DetailLink";
import { carrierAccessDetailPath } from "./extenderPopCarrierAccessData";

/** The id of the Backhaul, linked to its detail while the feature is on. */
const AccessId: React.FC<{ group: BackhaulGroup }> = ({ group }) => {
  if (!group.access) {
    return <span className="text-semantic-text-secondary">Not declared</span>;
  }
  return isCarrierAccessEnabled() ? (
    <DetailLink
      to={carrierAccessDetailPath(group.access.id)}
      label={group.access.serviceId}
    />
  ) : (
    <span>{group.access.serviceId}</span>
  );
};

/**
 * The Backhaul (Carrier Access) of a site or a port, one entry per sold
 * access: "84-7C1E20", its redundancy ("Dual · 1+1") and one line per leg
 * ("Primary: Fiber Optic · AXTEL"). The carriers of a site with no access
 * yet read as "Not declared". Shared by the CPE list and detail and the port
 * views so all read the same. Once there is an access, the undeclared
 * carriers are left to each device's links table.
 *
 * `partial`: the carriers are only some of the access's legs — a port sees
 * its own, while the access can span two ports — so the redundancy they
 * would add up to is wrong and is left to the Backhaul detail.
 * `compact`: one line per access, "84-7C1E20 · MCM", for a table cell.
 */
const CarrierAccessSummary: React.FC<{
  carriers: CarrierAccess[];
  align?: "start" | "end";
  partial?: boolean;
  compact?: boolean;
}> = ({ carriers, align = "start", partial = false, compact = false }) => {
  const groups = backhaulSummary(carriers);
  if (groups.length === 0) return <div>-</div>;

  if (compact) {
    return (
      <div className="flex flex-col leading-tight">
        {groups.map((group) => (
          <span
            key={group.access?.id ?? "undeclared"}
            className="whitespace-nowrap text-sm"
          >
            <AccessId group={group} />
            {group.names.length > 0 && (
              <span className="text-semantic-text-secondary">
                {" · "}
                {group.names.join(", ")}
              </span>
            )}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col gap-1.5 leading-tight ${
        align === "end" ? "items-end text-right" : ""
      }`}
    >
      {groups.map((group) => (
        <div key={group.access?.id ?? "undeclared"} className="flex flex-col">
          <span className="whitespace-nowrap text-sm">
            <AccessId group={group} />
          </span>
          {group.redundancy && !partial && (
            <span className="whitespace-nowrap text-xs text-semantic-text-primary">
              {group.redundancy}
            </span>
          )}
          {group.lines.map((line) => (
            <span
              key={line}
              className="whitespace-nowrap text-xs text-semantic-text-secondary"
            >
              {line}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
};

export default CarrierAccessSummary;
